import router from "@/router";
import RouteNames from "@/router/RouteNames.ts";
import PlaceTokens from "@/utils/constants/PlaceTokens.ts";
import ViewMode from "@/utils/constants/ViewMode.ts";
import SaveBehavior from "@/utils/constants/SaveBehavior.ts";
import PlaceUtils from "@/utils/common/PlaceUtils.ts";
import TopPanelSearchUtils, {SearchItem} from "@/utils/common/TopPanelSearchUtils.ts";
import ObjectChecker from "@/utils/common/ObjectChecker.ts";
import {useLoadingStore} from "@/stores/LoadingStore.ts";
import {useLoginInfoStore} from "@/stores/loginInfo.ts";
import {useErrorsStore, useMessagesStore} from "@/stores/ToastDataStore.ts";
import {useNamaFlagsStore} from "@/stores/nama-flags-store.ts";
import DocsCaptureMode from "@/utils/common/DocsCaptureMode.ts";
import {useToast} from "vue-toastification";

const AUTOMATION_API_VERSION = 2;
const DEFAULT_TIMEOUT_MS = 60000;
const POLL_INTERVAL_MS = 100;
// A single ready() sample is not enough: on a route change the loading counter settles before the
// next edit screen registers its presenter, so a lone sample can land in the gap between screens.
const CONSECUTIVE_READY_SAMPLES = 3;

export interface GotoOptions {
  entityType: string;
  id?: string;
  code?: string;
  view?: string;
  pageId?: number;
  focusOnField?: string;
  editViewName?: string;
  timeoutMs?: number;
}

export interface AutomationState {
  loggedIn: boolean;
  routeName: string;
  entityType?: string;
  view?: string;
  id?: string;
  pageId?: number;
  ready: boolean;
  loading: boolean;
  dirty: boolean;
  errorCount: number;
}

export default class NamaAutomation {
  static readonly version = AUTOMATION_API_VERSION;

  static isEnabled(): boolean {
    return (window as any).namaDebugMode === true;
  }

  static state(): AutomationState {
    const query = this.currentQuery();
    const presenter = this.editPresenter();
    return {
      loggedIn: useLoginInfoStore().isLoggedIn,
      routeName: String(router.currentRoute.value.name ?? ""),
      entityType: query[PlaceTokens.PRM_ENTITY_TYPE],
      view: query[PlaceTokens.PRM_VIEW],
      id: query[PlaceTokens.PRM_ID],
      pageId: this.parsedPageId(query[PlaceTokens.PRM_PAGE_ID]),
      ready: this.ready(),
      loading: this.isLoading(),
      dirty: presenter ? presenter.isDirty() === true : false,
      errorCount: useErrorsStore().errors.length,
    };
  }

  static ready(): boolean {
    if (!useLoginInfoStore().isLoggedIn)
      return false;
    if (this.isLoading())
      return false;
    if (!this.onEditRoute())
      return true;
    return this.editScreenLoaded();
  }

  // Deliberately independent of window.isScreenReady, which exists only while an edit screen is
  // mounted, and not at all before the 2026-08-23 EditView fix. These are the refs it itself reads.
  private static editScreenLoaded(): boolean {
    const presenter = this.editPresenter();
    return !!presenter && presenter.validLayout?.value === true && presenter.recordLoaded?.value === true;
  }

  static async whenReady(timeoutMs: number = DEFAULT_TIMEOUT_MS): Promise<AutomationState> {
    await this.waitForStable(() => this.ready(), timeoutMs, "screen did not become ready");
    return this.state();
  }

  static async login(user: string, password: string, timeoutMs: number = DEFAULT_TIMEOUT_MS): Promise<AutomationState> {
    if (useLoginInfoStore().isLoggedIn)
      return this.whenReady(timeoutMs);
    // loginByUserAndPassword is registered by the SignIn component and nulled on unmount, so it
    // exists only while the login form is actually showing.
    await this.waitFor(() => useLoginInfoStore().isLoggedIn || typeof (window as any).loginByUserAndPassword === "function",
      timeoutMs, "login form never appeared");
    if (!useLoginInfoStore().isLoggedIn)
      (window as any).loginByUserAndPassword(user, password);
    await this.waitFor(() => useLoginInfoStore().isLoggedIn, timeoutMs, "login did not complete");
    return this.whenReady(timeoutMs);
  }

  static async goto(options: GotoOptions): Promise<AutomationState> {
    const place = this.placeFor(options);
    if (!place)
      throw new Error("goto requires an entityType");
    if (ObjectChecker.isNotEmptyOrNull(options.code))
      place.query[PlaceTokens.PRM_CODE] = options.code;
    if (ObjectChecker.isNotNullOrUndefined(options.pageId))
      place.pageId = options.pageId;
    if (ObjectChecker.isNotEmptyOrNull(options.focusOnField))
      place.focusOnField = options.focusOnField;
    if (ObjectChecker.isNotEmptyOrNull(options.editViewName))
      place.editViewName = options.editViewName;
    if (ObjectChecker.isNotEmptyOrNull(options.view))
      place.view = options.view;
    const timeoutMs = options.timeoutMs ?? DEFAULT_TIMEOUT_MS;
    await this.push({name: RouteNames.EDIT_VIEW_ROUTE, query: place.fetchQuery()}, timeoutMs);
    return this.whenReady(timeoutMs);
  }

  static async gotoList(entityType: string, timeoutMs: number = DEFAULT_TIMEOUT_MS): Promise<AutomationState> {
    await this.push({name: RouteNames.LIST_VIEW_ROUTE, query: {[PlaceTokens.PRM_ENTITY_TYPE]: entityType}}, timeoutMs);
    return this.whenReady(timeoutMs);
  }

  static async searchMenu(query: string) {
    const matches = await TopPanelSearchUtils.searchFor(query);
    return (matches ?? []).map(item => this.serializableSearchItem(item));
  }

  static async openMenu(query: string, matchIndex: number = 0, timeoutMs: number = DEFAULT_TIMEOUT_MS): Promise<AutomationState> {
    const matches = await TopPanelSearchUtils.searchFor(query);
    const match = (matches ?? [])[matchIndex];
    if (!match)
      throw new Error("no menu match at index " + matchIndex + " for " + query);
    const route = TopPanelSearchUtils.resolveSearchItemRoute(match);
    if (!route)
      throw new Error("menu match does not resolve to a route: " + match.text);
    await this.push(route, timeoutMs);
    return this.whenReady(timeoutMs);
  }

  static fields(filter: string = "") {
    return TopPanelSearchUtils.searchForFields(filter).map(field => ({fieldId: field.id, label: field.text}));
  }

  static async focusField(fieldId: string, dontFlash?: boolean): Promise<boolean> {
    const matches = TopPanelSearchUtils.searchForFields(fieldId);
    if (!matches.length)
      return false;
    useNamaFlagsStore().setFieldToFocusOptions({field: matches[0], dontFlash});
    await this.whenReady();
    return true;
  }

  static getField(fieldId: string, row?: number): any {
    return this.toPlainValue(this.readPath(this.requireEditPresenter().fetchRecord(), fieldId, row));
  }

  static async setField(fieldId: string, value: any, row?: number) {
    const presenter = this.requireEditPresenter();
    this.writePath(presenter.fetchRecord(), fieldId, value, row);
    await presenter.fetchDispatcher().validate(fieldId, row);
    await this.whenReady();
    return {value: this.getField(fieldId, row), error: this.fieldError(fieldId)};
  }

  static fieldError(fieldId: string): string {
    return this.requireEditPresenter().fetchFieldErrorMsg(fieldId) ?? "";
  }

  static async save(behavior: string = SaveBehavior.SaveAndCommit) {
    const presenter = this.requireEditPresenter();
    useErrorsStore().clearErrors();
    const saved = await presenter.doSave(behavior, false, undefined, false);
    await this.whenReady();
    return {saved: saved === true, errors: this.errors(), state: this.state()};
  }

  static async runAction(actionId: string) {
    const presenter = this.requireEditPresenter();
    useErrorsStore().clearErrors();
    await presenter.callGUIAction(actionId);
    await this.whenReady();
    return {errors: this.errors(), state: this.state()};
  }

  static errors() {
    return useErrorsStore().errors.map((error: any) => ({
      message: error.message?.englishText || error.message?.arabicText || "",
      row: error.row,
      field: error.relatedToOption,
    }));
  }

  static messages(): string[] {
    return useMessagesStore().messages.map((m: any) => m.message?.englishText || m.message?.arabicText || "");
  }

  static clearErrors(): void {
    useErrorsStore().clearErrors();
  }

  // Suppresses the notification/announcement panel and the error and message toasts, so a
  // documentation screenshot shows only the screen being documented. Also settable with ?capture=1.
  static captureMode(on: boolean = true): boolean {
    DocsCaptureMode.set(on);
    if (on) {
      useErrorsStore().clearErrors();
      useMessagesStore().clearMessages();
      useToast().clear();
    }
    return DocsCaptureMode.isActive();
  }

  private static isLoading(): boolean {
    const loading = useLoadingStore();
    return loading.isFirstTimeLoadingActive || loading.timerRunning || loading.loadingCount > 0;
  }

  private static onEditRoute(): boolean {
    return router.currentRoute.value.name === RouteNames.EDIT_VIEW_ROUTE;
  }

  private static currentQuery(): Record<string, any> {
    return (router.currentRoute.value.query ?? {}) as Record<string, any>;
  }

  private static parsedPageId(raw: any): number | undefined {
    const pageId = Number.parseInt(String(raw));
    return Number.isNaN(pageId) ? undefined : pageId;
  }

  private static placeFor(options: GotoOptions) {
    const identified = ObjectChecker.isNotEmptyOrNull(options.id) || ObjectChecker.isNotEmptyOrNull(options.code);
    const place = identified
      ? PlaceUtils.editPlace(options.entityType, options.id)
      : PlaceUtils.newPlace(options.entityType);
    if (place && ObjectChecker.isEmptyOrNull(place.view))
      place.view = identified ? ViewMode.EDIT : ViewMode.NEW;
    return place;
  }

  // router.push never settles while a navigation guard is waiting on a modal, which is what happens
  // when you navigate away from a screen with unsaved changes. Bound it so that hangs as an error.
  private static async push(route: any, timeoutMs: number = DEFAULT_TIMEOUT_MS): Promise<void> {
    try {
      await this.withTimeout(router.push(route), timeoutMs,
        "navigation did not settle, an unsaved-changes confirmation is probably open; save or discard first");
    } catch (navigationRejected: any) {
      // vue-router rejects a push that resolves to the current location; that is a no-op, not a failure.
      if (!navigationRejected?.type)
        throw navigationRejected;
    }
  }

  private static withTimeout<T>(promise: Promise<T>, timeoutMs: number, description: string): Promise<T> {
    return Promise.race([promise, this.sleep(timeoutMs).then((): T => {
      throw new Error("timed out after " + timeoutMs + "ms: " + description);
    })]);
  }

  private static editPresenter(): any | undefined {
    const presenter: any = TopPanelSearchUtils.findAnyEditPresenter();
    return presenter?.isEditPresenter?.() ? presenter : undefined;
  }

  private static requireEditPresenter(): any {
    const presenter = this.editPresenter();
    if (!presenter)
      throw new Error("no edit screen is open");
    return presenter;
  }

  private static readPath(record: Record<string, any>, fieldId: string, row?: number): any {
    if (ObjectChecker.isEmptyOrNull(row) && row !== 0)
      return record[fieldId];
    const path = this.splitGridPath(fieldId);
    return record[path.collectionId]?.[row as number]?.[path.columnId];
  }

  private static writePath(record: Record<string, any>, fieldId: string, value: any, row?: number): void {
    if (ObjectChecker.isEmptyOrNull(row) && row !== 0) {
      record[fieldId] = value;
      return;
    }
    const path = this.splitGridPath(fieldId);
    const line = record[path.collectionId]?.[row as number];
    if (!line)
      throw new Error("no line " + row + " in collection " + path.collectionId);
    line[path.columnId] = value;
  }

  private static splitGridPath(fieldId: string) {
    const separator = fieldId.lastIndexOf(".");
    if (separator < 0)
      throw new Error("grid field id must be <collection>.<column>, got " + fieldId);
    return {collectionId: fieldId.slice(0, separator), columnId: fieldId.slice(separator + 1)};
  }

  private static serializableSearchItem(item: SearchItem) {
    return {
      id: item.id,
      type: item.type,
      text: item.text,
      code: item.code,
      entityType: item.record?.entityType,
      recordId: item.record?.id,
    };
  }

  // browser_evaluate serializes whatever it gets back, and records are reactive proxies that can
  // carry functions and cycles, so hand back a plain snapshot instead.
  private static toPlainValue(value: any): any {
    if (value === null || typeof value !== "object")
      return value;
    try {
      return JSON.parse(JSON.stringify(value));
    } catch (notSerializable) {
      return String(value);
    }
  }

  private static async waitFor(condition: () => boolean, timeoutMs: number, description: string): Promise<void> {
    const deadline = Date.now() + timeoutMs;
    while (Date.now() < deadline) {
      if (condition())
        return;
      await this.sleep(POLL_INTERVAL_MS);
    }
    throw new Error("timed out after " + timeoutMs + "ms: " + description);
  }

  private static async waitForStable(condition: () => boolean, timeoutMs: number, description: string): Promise<void> {
    const deadline = Date.now() + timeoutMs;
    let consecutive = 0;
    while (Date.now() < deadline) {
      consecutive = condition() ? consecutive + 1 : 0;
      if (consecutive >= CONSECUTIVE_READY_SAMPLES)
        return;
      await this.sleep(POLL_INTERVAL_MS);
    }
    throw new Error("timed out after " + timeoutMs + "ms: " + description);
  }

  private static sleep(ms: number): Promise<void> {
    return new Promise(resolve => setTimeout(resolve, ms));
  }
}
